/**
 * A Common Org entity (organization context) the current user is a member of.
 * `commonOrgId` is the FGA `operating-entity` identifier; `displayName` is the
 * enriched, human-readable org name. Returned by the BFF
 * `GET /context/memberships/mine` endpoint and consumed by the Org Context
 * Selector. See references/contracts.md §2.
 */
export interface CommonOrgEntity {
  commonOrgId: string;
  displayName: string;
}

/**
 * Response body of `GET /context/memberships/mine`. `entities` is sorted
 * alphabetically by `displayName` server-side.
 */
export interface CommonOrgEntities {
  entities: CommonOrgEntity[];

  /**
   * Whether the selector should offer type-ahead search instead of a dropdown.
   *
   * When true, `entities` may be empty: the searchable set is not enumerated in the
   * response. That is the case for a user whose context spans more orgs than a
   * dropdown can usefully hold.
   *
   * Optional because this component and the BFF deploy independently and in either
   * order, so an older BFF response has no such field. Read it as `=== true`: a missing
   * value, and any value that is not the boolean `true`, means "not search mode".
   */
  showSearch?: boolean;

  /**
   * The entity to select initially when `showSearch` is true.
   *
   * ABSENT rather than null when there is no default, so presence alone does not
   * mean search mode: read `showSearch` for that.
   */
  defaultEntity?: CommonOrgEntity;
}

export type MembershipDataContextValue =
  | {status: "pending"}
  | {status: "complete"; data: CommonOrgEntities}
  | {status: "error"; error: Error};

/**
 * Runtime type guard for one entity. Exported because the entity search response carries the
 * same entities and validates them the same way — see `orgSearchApi.ts`.
 */
export function isCommonOrgEntity(value: unknown): value is CommonOrgEntity {
  return (
    typeof value === "object" &&
    value !== null &&
    typeof (value as CommonOrgEntity).commonOrgId === "string" &&
    typeof (value as CommonOrgEntity).displayName === "string"
  );
}

/**
 * Runtime type guard for the `GET /context/memberships/mine` response body.
 * A TypeScript `as` cast verifies nothing at runtime, so the data provider
 * validates the BFF response shape before handing it to consumers; a malformed
 * response degrades to an error state rather than propagating an
 * unchecked shape to the selector.
 *
 * `defaultEntity` is validated only when present. Requiring it would reject every response
 * from an older BFF, which is a real state: this component and the BFF deploy independently
 * and in either order. `showSearch` is not validated at all — see the note in the body.
 */
export function isCommonOrgEntities(
  value: unknown
): value is CommonOrgEntities {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const candidate = value as CommonOrgEntities;
  if (
    !Array.isArray(candidate.entities) ||
    !candidate.entities.every(isCommonOrgEntity)
  ) {
    return false;
  }
  // showSearch is deliberately NOT validated. Its only reader tests `=== true`
  // (OrgContextSelectorElement#defaultEntity), so any unknown value already reads as "not search
  // mode" and lands on the legacy dropdown path — the compat behaviour this guard exists to
  // protect. Rejecting it instead would put every user of that BFF version into the error state
  // to guard a value that is already safe.
  //
  // defaultEntity keeps its check: its contents are emitted in contextChanged, so a malformed
  // entity would propagate `displayName: undefined` out to adopting surfaces. Rejecting an
  // explicit null is part of that — the BFF omits the field instead.
  return (
    candidate.defaultEntity === undefined ||
    isCommonOrgEntity(candidate.defaultEntity)
  );
}
